import React, { useEffect, useRef, useState } from "react";
import {
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import { ChevronLeft, X } from "lucide-react";
import { BsPersonLinesFill } from "react-icons/bs";
import logo from "../assets/Logo icon.png";
import DOMPurify from "dompurify";
import { useDispatch, useSelector } from "react-redux";
import {
  updateConvoMessage,
  updateConvoStatus,
} from "../Redux/Actions/convoActions";
import { getSocket } from "../utils/useChatSocket";

import { getFallbackBackView } from "../utils/navigationHelper";

const socket = getSocket();
function Convo({ setView, setActiveIcon, setupDetails, setIsOpen }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentType, setAttachmentType] = useState(null);
  const [smooth, setSmooth] = useState(false);
  const convo = useSelector((state) => state.convo);
  const dispatch = useDispatch();

  const handleBack = () => {
    const fallback = getFallbackBackView(setupDetails);
    setView(fallback.view);
    if (setActiveIcon) setActiveIcon(fallback.icon);
  };

  const messagesEndRef = useRef(null);
  const scrollToBottom = (smooth) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  useEffect(() => {
    setSmooth(true);
  }, []);

  useEffect(() => {
    scrollToBottom(smooth);
  }, [convo?.messages]);

  const getRawText = (htmlString) => {
    const doc = new DOMParser().parseFromString(htmlString, "text/html");
    return doc.body.textContent || doc.body.innerText || "";
  };

  const removeQuotedText = (message) => {
    const quotedTextPattern =
      /(?:^On .+ wrote:|^From: .+|^\s*>.*(?:\n|$)|^>.*(?:\n|$))/gms;
    const result = message.replace(quotedTextPattern, "").trim();

    return getRawText(result);
  };

  useEffect(() => {
    const handleReceiveMessage = (messageData) => {
      console.log({ messageData });
    };

    const handleMessage = (message) => {
      console.log({ message });
      console.log({ convo });
      dispatch(updateConvoMessage(message));
    };

    const handleTicketStatusUpdated = (response) => {
      dispatch(updateConvoStatus(response.status));
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("message", handleMessage);
    socket.on("ticketStatusUpdated", handleTicketStatusUpdated);

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("message", handleMessage);
      socket.off("ticketStatusUpdated", handleTicketStatusUpdated);
    };
  }, [convo]);

  useEffect(() => {
    if (convo) {
      const handleConnect = () => {
        const allRoomIds = [convo.roomId];
        socket.emit("join", allRoomIds);
      };

      socket.on("connect", handleConnect);
      
      if (socket.connected) {
        handleConnect();
      }

      return () => {
        socket.off("connect", handleConnect);
      };
    }
  }, [convo]);

  const sendMessage = () => {
    if (convo === null) {
      console.error("Can not send message, convo missing!");
      return;
    }
    const inputMessage = message.trim();
    if (inputMessage || (attachment && attachmentType)) {
      const msg = {
        room: convo.roomId,
        sender: "Customer",
        senderId: "",
        message: inputMessage === "" ? null : inputMessage,
        attachment,
        attachmentType,
      };
      socket.emit("message", msg);
      setMessage("");
      setAttachment(null);
      setAttachmentType(null);
    }
  };

  return (
    <div className="flex flex-col h-full flex-1 min-h-0 bg-white">
      {/* Gleap Enterprise White Header */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100 shrink-0 bg-white sticky top-0 z-10">
        <button
          onClick={handleBack}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Back"
        >
          <ChevronLeft className="w-5 h-5 stroke-[2.2]" />
        </button>

        <div className="font-bold text-slate-900 text-sm tracking-tight">
          Messages
        </div>

        <button
          onClick={() => {
            if (setIsOpen) setIsOpen(false);
          }}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="relative flex-grow flex flex-col min-h-0">
        <div
          style={{
            overflowY: "auto",
            maxHeight: "50vh",
            width: "100%",
            msOverflowStyle: "none", // For IE and Edge
            scrollbarWidth: "none", // For Firefox
          }}
        >
          <div className="flex flex-col items-center">
            <div className="flex items-center">
              <BsPersonLinesFill
                className="w-14 h-14 mr-1"
                style={{
                  color: setupDetails?.widgetCustomizations?.primaryColor,
                }}
              />

              <div className="h-2 w-2 mr-2 bg-green-500 rounded-xl"></div>
              <div className="text-sm font-semibold">Live agent</div>
            </div>
          </div>
          <div className="flex">
            <BsPersonLinesFill
              className="w-7 h-7 mt-auto mr-1"
              style={{
                color: setupDetails?.widgetCustomizations?.primaryColor,
              }}
            />
            <div>
              <div className="border rounded-xl font-normal text-xs text-black bg-gray-200 p-3 w-72 text-start">
                <span className="block mt-0">
                  You’re speaking with Live Agent - I’m here to answer your
                  questions.You can also talk to the team if you need to.
                </span>
              </div>
              <div className="flex justify-start items-center gap-0 mt-2">
                <div className="flex border rounded-e-xl rounded-t-xl font-normal text-xs text-black bg-gray-200 p-2 text-start">
                  <span className="text-xs">How can i help?</span>
                  {convo && (
                    <p className="text-xs text-gray-500 ml-1   mb-0 mt-1">
                      {new Date(convo?.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {convo &&
            convo.messages.map(
              (msg, index) =>
                msg.type !== "Internal" &&
                msg.message &&
                (msg.sender === "Customer" ? (
                  <div
                    key={index}
                    className="flex justify-end ml-[20%] items-center mt-2"
                  >
                    <div
                      style={{
                        background: `linear-gradient(90deg, ${
                          setupDetails?.widgetCustomizations?.color1 ||
                          setupDetails?.widgetCustomizations?.primaryColor ||
                          "#4c73de"
                        } 0%,  ${
                          setupDetails?.widgetCustomizations?.color2 ||
                          setupDetails?.widgetCustomizations?.secondaryColor ||
                          "#2a4dad"
                        } 100%)`,
                        backgroundClip: "text",
                        color: "transparent",
                      }}
                      className={`font-normal text-sm text-black text-start border p-2 rounded-s-xl ${
                        convo.messages[index + 1]?.sender === msg.sender &&
                        "rounded-b-xl"
                      } rounded-t-xl bg-blue-200 break-words whitespace-normal relative min-w-[6vw] mr-2`}
                    >
                      <span
                        className="pr-14"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            removeQuotedText(msg.message)
                          ),
                        }}
                      ></span>
                      <span className="text-xs text-gray-500 absolute bottom-1 right-1 whitespace-nowrap">
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div key={index} className="flex items-center">
                    {convo.messages[index + 1]?.sender !== msg.sender ? (
                      <BsPersonLinesFill
                        className="w-7 h-7 mt-auto mr-1"
                        style={{
                          color:
                            setupDetails?.widgetCustomizations?.primaryColor,
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 mt-auto"></div>
                    )}
                    <div
                      key={index}
                      className="flex justify-start items-center mt-2"
                    >
                      <div
                        className={`ml-0 font-normal text-sm text-black text-start border p-2 rounded-e-xl ${
                          convo.messages[index + 1]?.sender === msg.sender &&
                          "rounded-b-xl"
                        } rounded-t-xl bg-gray-200 break-words whitespace-normal relative min-w-[6vw]`}
                      >
                        <span
                          className="pr-14"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              removeQuotedText(msg.message)
                            ),
                          }}
                        ></span>
                        <span className="text-xs text-gray-500 absolute bottom-1 right-1 whitespace-nowrap">
                          {new Date(msg.timestamp).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
            )}

          {/* Dummy div to scroll into view */}
          <div ref={messagesEndRef}></div>
        </div>
      </div>
      <div className="px-3 pb-20 pt-2 relative bg-white w-full">
        <div className="relative rounded-full border border-slate-200/90 hover:border-slate-300 focus-within:border-slate-400 bg-white w-full max-w-full mx-auto transition-colors shadow-sm">
          <input
            type="text"
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full py-2.5 px-4 pr-12 rounded-full border-none text-xs text-slate-800 focus:outline-none bg-transparent"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                sendMessage();
                e.preventDefault();
              }
            }}
          />

          <div className="absolute inset-y-0 right-1 flex items-center pr-2">
            <button
              onClick={() => sendMessage()}
              disabled={!message.trim()}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all border-none outline-none ${
                message.trim()
                  ? "text-white hover:scale-105 cursor-pointer shadow-sm"
                  : "bg-slate-100 text-slate-300 cursor-not-allowed"
              }`}
              style={message.trim() ? { backgroundColor: setupDetails?.widgetCustomizations?.uiColor || setupDetails?.widgetCustomizations?.color1 || "#000000" } : {}}
            >
              <PaperAirplaneIcon className="w-3.5 h-3.5 -rotate-45" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Convo;
